import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { LoadingSpinner } from './Layout';
import { Play, Volume2, Square, ArrowRight } from 'lucide-react';
import useNarration from '../hooks/useNarration';
import { useAuth } from '../context/AuthContext';
import { useExperienceMode } from '../context/ExperienceModeContext';
import { hasSeenArrival, markArrivalSeen, registerArrivalKey } from '../lib/arrivals';

/**
 * The arrival moment. Every major page opens on one of these: centred, glowing,
 * says what the page is for, then hands over with a single button.
 */
export const PageArrival = ({
  eyebrow, title, subtitle, accent = '#22d3ee', second = '#a78bfa',
  facts = [], modules = [], modulesTitle = 'What you get here',
  ctaLabel = 'Continue', onStart, secondary, narration, footnote, testid = 'page-arrival',
}) => {
  const n = useNarration();
  const { token } = useAuth();
  const { isDemo } = useExperienceMode();
  // Seen it before? Walk straight in. School Demo Mode always walks straight in,   // the arrival screens are the intermediary pages the district demo skips.
  const [skip] = useState(() => isDemo || hasSeenArrival(testid));
  const skipped = useRef(false);

  useEffect(() => {
    registerArrivalKey(window.location.pathname, testid);
    if (skip && !skipped.current) { skipped.current = true; onStart?.(); }
  }, [skip, onStart, testid]);

  const enter = () => { markArrivalSeen(testid, token); onStart?.(); };

  if (skip) return null;

  return (
    <div data-testid={testid}>
      <div className="relative overflow-hidden rounded-3xl border bg-[#0a1729]/60 p-8 md:p-12 text-center"
        style={{ borderColor: `${accent}33`, boxShadow: `0 40px 120px -60px ${accent}` }}>
        <div className="absolute -top-32 -right-24 w-[28rem] h-[28rem] rounded-full blur-3xl opacity-25"
          style={{ background: `radial-gradient(circle, ${accent}, transparent 65%)` }} />
        <div className="absolute -bottom-40 -left-24 w-[26rem] h-[26rem] rounded-full blur-3xl opacity-20"
          style={{ background: `radial-gradient(circle, ${second}, transparent 65%)` }} />

        <div className="relative max-w-3xl mx-auto">
          {eyebrow && (
            <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="text-[11px] uppercase tracking-[0.24em]" style={{ color: accent }}>{eyebrow}</motion.p>
          )}
          <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
            className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mt-3">
            {title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
            className="text-base md:text-lg text-slate-300/90 mt-5 leading-relaxed">
            {subtitle}
          </motion.p>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="flex flex-wrap items-center justify-center gap-3 mt-9">
            <Button onClick={enter} data-testid="arrival-start-btn"
              className="rounded-full px-8 py-6 text-base text-white"
              style={{ background: `linear-gradient(90deg, ${accent}, ${second})` }}>
              <Play className="w-4 h-4 mr-2" />{ctaLabel}
            </Button>
            {narration && (
              <Button variant="outline" size="sm" data-testid="arrival-listen-btn"
                onClick={() => (n.speaking ? n.stop() : n.say(narration))}
                className="border-slate-700 text-slate-200 rounded-full">
                {n.loading ? <LoadingSpinner size="sm" />
                  : n.speaking ? <Square className="w-4 h-4 mr-2" /> : <Volume2 className="w-4 h-4 mr-2" />}
                {n.speaking ? 'Stop' : 'Listen'}
              </Button>
            )}
            {secondary}
          </motion.div>

          {facts.length > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.26 }}
              className="grid sm:grid-cols-3 gap-3 mt-10" data-testid="arrival-facts">
              {facts.map((f) => (
                <div key={f.head} className="rounded-2xl border border-slate-700/60 bg-slate-900/50 px-4 py-4">
                  {f.icon && <f.icon className="w-5 h-5 mx-auto" style={{ color: accent }} />}
                  <p className="text-sm font-semibold text-white mt-2.5">{f.head}</p>
                  {f.sub && <p className="text-xs text-slate-300 mt-1">{f.sub}</p>}
                </div>
              ))}
            </motion.div>
          )}

          {footnote && <p className="text-xs text-slate-300 mt-7 max-w-2xl mx-auto">{footnote}</p>}
        </div>
      </div>

      {modules.length > 0 && (
        <div className="mt-10">
          <h2 className="font-outfit text-base md:text-lg font-semibold text-white text-center">{modulesTitle}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5" data-testid="arrival-modules">
            {modules.map((m, i) => (
              <motion.div key={m.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.06 * i, duration: 0.35 }}
                className="rounded-2xl border p-5 bg-slate-900/50"
                style={{ borderColor: `${m.hue || accent}33`, boxShadow: `0 24px 60px -50px ${m.hue || accent}` }}>
                {m.icon && (
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: `${m.hue || accent}1f`, border: `1px solid ${m.hue || accent}44` }}>
                    <m.icon className="w-5 h-5" style={{ color: m.hue || accent }} />
                  </div>
                )}
                <p className="font-outfit text-white font-semibold mt-4">{m.label}</p>
                <p className="text-xs text-slate-300 mt-1.5 leading-relaxed">{m.line}</p>
              </motion.div>
            ))}
          </div>
          <div className="flex justify-center mt-8">
            <Button onClick={enter} data-testid="arrival-start-bottom-btn"
              className="rounded-full px-7 text-white" style={{ background: `linear-gradient(90deg, ${accent}, ${second})` }}>
              {ctaLabel} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageArrival;
